/**
 * Cross-environment DOM bootstrap.
 *
 * In the browser, `DOMParser`, `HTMLElement`, `SVGElement`, etc. are already
 * on `globalThis`; this module is a no-op. On Node, the first time it
 * executes it lazily loads jsdom and mirrors a small set of essentials
 * onto `globalThis` so the rest of the html-editor (parse.ts,
 * resolve-ad-refs.ts) can use `new DOMParser()` / `instanceof HTMLElement` /
 * `Node.TEXT_NODE` the same way it would in a browser. Elements produced by
 * `new DOMParser().parseFromString(...)` from a JSDOM window inherit from
 * that window's class hierarchy, so unqualified `instanceof` checks line up
 * against the globals we mirror here.
 *
 * The jsdom require is hidden behind `eval("require")` so a static bundler
 * (Vite, etc.) building the app for the browser never tries to pull jsdom
 * into the browser bundle. The browser branch returns before that line.
 *
 * Import this file for its side effect from any module that touches the
 * shared DOM globals.
 */

const GLOBAL_NAMES = [
  "DOMParser",
  "Document",
  "Node",
  "Element",
  "HTMLElement",
  "HTMLImageElement",
  "SVGElement",
] as const;

const g = globalThis as unknown as Record<string, unknown>;

// Minimal structural type for the jsdom API we use — jsdom ships no bundled
// types and `@types/jsdom` isn't installed, so referencing
// `typeof import("jsdom")` fails under strict consumers (the ad-studio
// renderer bundle typechecks this file with `strict: true`).
type JsdomModule = {
  JSDOM: new (html?: string, options?: { url?: string }) => { window: object };
};

if (typeof g.DOMParser === "undefined") {
  // eslint-disable-next-line @typescript-eslint/no-implied-eval -- see file header
  const req = eval("require") as (m: string) => unknown;
  const { JSDOM } = req("jsdom") as JsdomModule;
  const window = new JSDOM("", { url: "http://localhost/" }).window;
  const w = window as unknown as Record<string, unknown>;
  for (const name of GLOBAL_NAMES) {
    if (typeof g[name] === "undefined") g[name] = w[name];
  }
}
